// La ruta del modo Diseño de indumentaria — ADR-2026-09-17-un-diseno-es-un-chat.
// Sin `:disenoId` es la portada; con él, el lienzo del diseño. Gate por rubro adentro del
// componente, como Belleza: la card no se muestra fuera de Moda pero la URL existe. Y
// piso de plan Pro/Ultra, el mismo que ponen las listas con `getModePlanLock`, vuelto a
// mirar acá porque la URL se puede escribir a mano
// (ADR-2026-09-18-el-modo-diseno-se-abre-a-moda-desde-pro; los dev testers pasan, como
// en todos los candados). Sólo escritorio (el lienzo lo es): en mobile la card se ve y
// acá se avisa que se usa desde la computadora.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Lock, Monitor } from 'lucide-react';
import { useEsEscritorio } from '../../../../chat-atlas/ui/useEsEscritorio';
import { useRubroMarcaActiva } from '../../../../hooks/useRubroMarca';
import { useUserGroup } from '../../../../hooks/useUserGroup';
import { getModePlanLock, type ModePlanLock } from '../../../../lib/planCapabilities';
import { getUserPlan } from '../../../../lib/planUtils';
import { esRubroModa } from '../../../../lib/rubro';
import { LienzoDeDiseno } from './LienzoDeDiseno';
import { PortadaDeDiseno } from './PortadaDeDiseno';

const DisenoIndumentariaMode: React.FC = () => {
  const { disenoId } = useParams<{ disenoId?: string }>();
  const navigate = useNavigate();
  const esEscritorio = useEsEscritorio();
  const { rubro, loaded } = useRubroMarcaActiva();
  // `null` mientras no se sabe: un dev tester pasa sin mirar el plan.
  const { isDevTester } = useUserGroup();
  // El plan real de `company_profiles`; mientras no se conoce no se bloquea (el mismo
  // criterio que `getModePlanLock` con plan desconocido).
  const [plan, setPlan] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    getUserPlan().then((p) => {
      if (vivo && p) setPlan(p);
    });
    return () => {
      vivo = false;
    };
  }, []);
  const lock: ModePlanLock | null = isDevTester ? null : getModePlanLock('diseno-indumentaria', plan);

  // Con el rubro resuelto y sin ser Moda, a la home; sin resolver no se rebota (echar a
  // quien califica es peor que un instante en blanco).
  useEffect(() => {
    if (loaded && !esRubroModa(rubro)) navigate('/interfaz-v2', { replace: true });
  }, [loaded, rubro, navigate]);

  if (!esEscritorio) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center">
        <Monitor className="h-8 w-8 text-[#02062B]/[0.4]" aria-hidden="true" />
        <p className="text-[16px] font-semibold text-brand-black">Disponible en computadora</p>
        <p className="text-[14px] text-[#3C3C43]/60">Diseño de indumentaria se usa desde una computadora: el lienzo necesita pantalla.</p>
      </div>
    );
  }
  if (lock) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center">
        <Lock className="h-8 w-8 text-[#02062B]/[0.4]" aria-hidden="true" />
        <p className="text-[16px] font-semibold text-brand-black">{lock.title}</p>
        <p className="max-w-md text-[14px] text-[#3C3C43]/60">{lock.message}</p>
        <button
          type="button"
          onClick={() => navigate('/subscription')}
          className="mt-2 rounded-md bg-brand-primary px-5 py-2.5 text-[15px] font-semibold text-white transition-colors hover:bg-brand-primary-hover"
        >
          Ver planes
        </button>
      </div>
    );
  }
  return disenoId ? <LienzoDeDiseno disenoId={disenoId} /> : <PortadaDeDiseno />;
};

export default DisenoIndumentariaMode;
